import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Button,
  CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { toast } from 'react-toastify';
import IconToast from '../../../components/NewToast';
import { Browser } from '@capacitor/browser';
import { withRouter } from 'react-router';

const GoogleLoginButton = ({ intl, history, loginUser, loginPhase, loginMessage, userDetails, disabled }) => {
  const [loading, setLoading] = useState(false);
  const pendingGoogleLogin = useRef(false);
  const prevLoginPhase = useRef(loginPhase);
  const initialized = useRef(false);
  const googleIdTokenRef = useRef(null);
  const gisReadyRef = useRef(false);
  const theme = useTheme();

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    if (Capacitor.isNativePlatform()) {
      SocialLogin.initialize({
        google: {
          webClientId: process.env.GOOGLE_CLIENT_ID,
          iOSClientId: process.env.GOOGLE_IOS_CLIENT_ID,
        }
      });
    } else {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        gisReadyRef.current = true;
      };
      document.head.appendChild(script);
    }
  }, []);

  useEffect(() => {
    const prev = prevLoginPhase.current;
    prevLoginPhase.current = loginPhase;

    if (!pendingGoogleLogin.current) return;

    if (loginPhase === false && prev !== false) {
      pendingGoogleLogin.current = false;
      if (Capacitor.getPlatform() === 'ios') {
        toast.info(IconToast('error', intl.formatMessage({ id: 'no-portfoplus-account-bound-please-contact-us' })), { className: 'new-toast' });
      } else if (loginMessage === 'google-not-bound') {
        const params = new URLSearchParams({ campaignCode: 'google' });
        if (userDetails?.email) {
          params.set('email', userDetails.email);
        }
        if (userDetails?.emailToken) {
          params.set('emailToken', userDetails.emailToken);
        }
        if (userDetails?.name) {
          params.set('name', userDetails.name);
        }
        if (Capacitor.isNativePlatform()) {
          params.set('source', 'capacitor');
        }
        history.push(`/userType?${params.toString()}`);
      }
      setLoading(false);
    }

    if (loginPhase === true && prev !== true) {
      pendingGoogleLogin.current = false;
      setLoading(false);
    }
  }, [loginPhase, loginMessage, userDetails]);

  const handleGoogleLoginNative = useCallback(async () => {
    const result = await SocialLogin.login({
      provider: 'google',
      options: { scopes: ['email', 'profile'] }
    });
    return result && result.result && result.result.idToken;
  }, []);

  const handleGoogleLoginWeb = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!gisReadyRef.current || !window.google) {
        reject(new Error('Google Identity Services not loaded'));
        return;
      }
      const client = window.google.accounts.oauth2.initCodeClient({
        client_id: process.env.GOOGLE_CLIENT_ID,
        scope: 'email profile openid',
        ux_mode: 'popup',
        callback: (response) => {
          if (response.error) {
            reject(new Error(response.error));
            return;
          }
          resolve(response.code);
        },
        error_callback: () => {
          reject(new Error('popup_closed'));
        },
      });
      client.requestCode();
    });
  }, []);

  const handleGoogleLogin = useCallback(async () => {
    setLoading(true);

    try {
      let idToken;

      if (Capacitor.isNativePlatform()) {
        idToken = await handleGoogleLoginNative();

        if (!idToken) {
          toast.info(IconToast('error', intl.formatMessage({ id: 'google-authentication-failed' })), {
            className: 'new-toast'
          });
          setLoading(false);
          return;
        }

        pendingGoogleLogin.current = true;
        googleIdTokenRef.current = idToken;
        loginUser({ googleIdToken: idToken });
      } else {
        const code = await handleGoogleLoginWeb();

        if (!code) {
          toast.info(IconToast('error', intl.formatMessage({ id: 'google-authentication-failed' })), {
            className: 'new-toast'
          });
          setLoading(false);
          return;
        }

        pendingGoogleLogin.current = true;
        loginUser({ googleAuthCode: code });
      }
    } catch (error) {
      console.error('Google login error:', error);
      setLoading(false);
    }
  }, [intl, loginUser, handleGoogleLoginNative, handleGoogleLoginWeb]);

  return (
    <>
      <Button
        onClick={handleGoogleLogin}
        disabled={disabled || loading}
        style={{ minWidth: 'unset', padding: 6, borderRadius: '50%' }}
      >
        {loading ? (
          <div style={{ width: 30, height: 30, borderRadius: '50%', backgroundColor: '#F5F5F5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={20} />
          </div>
        ) : (
          <div style={{ width: 30, height: 30, borderRadius: '50%', backgroundColor: '#F5F5F5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src="/img/google-logo.svg" alt="Google" style={{ width: 20, height: 20 }} />
          </div>
        )}
      </Button>
    </>
  );
};

export default withRouter(GoogleLoginButton);
